import { studySummaryPresentation } from '../hooks/study-builder/studyAuthoringPresentation'
import type { SavedStudyBuilderDraftSummary } from '../hooks/study-builder/studyBuilderDrafts'
import { shopifyComponentCopy } from '@testfeed/product-language'
import type { ShopifyAuthoredTreatmentType } from '@testfeed/shopify-contracts'
import type { ShopifyResourceIndexAppliedFilter, ShopifyResourceIndexFacet } from './ShopifyResourceIndex'
import { STUDY_TREATMENT_TYPE_OPTIONS } from './study-setup-index'
import type { StudySetupPageState } from './study-setups-page-controller-types'
import { getStudySetupStatusByKey, isStudySetupStatusKey, type StudySetupStatusKey } from './study-setup-lifecycle'

export type CommitStudySetupsState = (changes: StudySetupPageState) => void

export function buildStudySetupsPageControls(
  studies: SavedStudyBuilderDraftSummary[],
  selectedProductIds: string[],
  selectedAudienceValues: string[],
  selectedTreatmentTypes: ShopifyAuthoredTreatmentType[],
  selectedStatusKeys: StudySetupStatusKey[],
  commitState: CommitStudySetupsState,
) {
  const productOptions = studies.flatMap((study) =>
    study.productId && study.productTitle ? [{ value: study.productId, label: study.productTitle }] : [],
  )
  const productChoices = [...new Map(productOptions.map((choice) => [choice.value, choice])).values()].toSorted(
    (left, right) => left.label.localeCompare(right.label),
  )
  const productLabels = new Map(productChoices.map((choice) => [choice.value, choice.label]))
  const audienceOptions = studies.flatMap((study) => (study.audience ? [study.audience] : []))
  const audienceChoices = [...new Map(audienceOptions.map((choice) => [choice.value, choice])).values()].toSorted(
    (left, right) => left.label.localeCompare(right.label, undefined, { sensitivity: 'base' }),
  )
  const statusOptions = studies.map((study) => {
    const status = studySummaryPresentation(study)
    return { value: status.key, label: status.label }
  })
  const statusChoices = [...new Map(statusOptions.map((choice) => [choice.value, choice])).values()].toSorted(
    (left, right) => left.label.localeCompare(right.label),
  )
  const audienceLabels = new Map(audienceChoices.map((choice) => [choice.value, choice.label]))
  const filters: ShopifyResourceIndexFacet[] = [
    {
      key: 'product',
      label: shopifyComponentCopy.studySetupsPage.productHeading023,
      options: productChoices,
      values: selectedProductIds,
      onChange: (values) => commitState({ product: values, page: 1 }),
    },
    {
      key: 'audience',
      label: shopifyComponentCopy.studySetupsPage.audienceHeading014,
      options: audienceChoices,
      values: selectedAudienceValues,
      onChange: (values) => commitState({ audience: values, page: 1 }),
    },
    {
      key: 'treatment-type',
      label: shopifyComponentCopy.studySetupsPage.treatmentTypeHeading028,
      options: STUDY_TREATMENT_TYPE_OPTIONS,
      values: selectedTreatmentTypes,
      onChange: (values) => commitState({ treatment: values, page: 1 }),
    },
    {
      key: 'lifecycle',
      label: shopifyComponentCopy.studySetupsPage.statusHeading013,
      options: statusChoices,
      values: selectedStatusKeys,
      onChange: (values) => commitState({ lifecycle: values.filter(isStudySetupStatusKey), page: 1 }),
    },
  ]
  const appliedFilters: ShopifyResourceIndexAppliedFilter[] = []
  const add = (key: string, label: string, clear: StudySetupPageState) =>
    appliedFilters.push({ key, label, onRemove: () => commitState(clear) })
  const audienceLabelsSelected = selectedAudienceValues
    .map((value) => audienceLabels.get(value))
    .filter((label): label is string => Boolean(label))
  if (audienceLabelsSelected.length > 0)
    add(
      'audience',
      `${shopifyComponentCopy.studySetupsPage.audienceHeading014}: ${audienceLabelsSelected.join(', ')}`,
      { audience: [], page: 1 },
    )
  const productLabelsSelected = selectedProductIds
    .map((value) => productLabels.get(value))
    .filter((label): label is string => Boolean(label))
  if (productLabelsSelected.length > 0)
    add('product', `${shopifyComponentCopy.studySetupsPage.productHeading023}: ${productLabelsSelected.join(', ')}`, {
      product: [],
      page: 1,
    })
  const treatmentLabels = new Map(STUDY_TREATMENT_TYPE_OPTIONS.map((option) => [option.value, option.label]))
  if (selectedTreatmentTypes.length > 0)
    add(
      'treatment-type',
      `${shopifyComponentCopy.studySetupsPage.treatmentTypeHeading028}: ${selectedTreatmentTypes.map((value) => treatmentLabels.get(value)).join(', ')}`,
      { treatment: [], page: 1 },
    )
  if (selectedStatusKeys.length > 0)
    add(
      'lifecycle',
      `${shopifyComponentCopy.studySetupsPage.statusHeading013}: ${selectedStatusKeys.map((key) => getStudySetupStatusByKey(key).label).join(', ')}`,
      { lifecycle: [], page: 1 },
    )
  return { filters, appliedFilters }
}
